import { cn } from '@/lib/utils'
import LinkWithTransition from '@/v2/components/helpers/link-with-transition'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import SectionHead from '@/v2/components/sections/landing-pages/section-head'
import { Button } from '@/v2/components/ui/button'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { CardType } from '@/v2/types/cards'
import { domAnimation, LazyMotion, m } from 'framer-motion'
import React from 'react'

interface WhyWeSectionProps {
  title: string
  description?: string
  cards: CardType[]
  className?: string
  isBgDark?: boolean
  ctaButton?: { href: string; text: string }
  sectionTitleClassName?: string
  imgClassName?: string
}

const WhyWeSection: React.FC<WhyWeSectionProps> = props => {
  const {
    title,
    description,
    cards,
    className,
    ctaButton,
    isBgDark = false,
    sectionTitleClassName,
    imgClassName
  } = props

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
  }

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const cardBackground = isBgDark ? 'bg-[#fff]' : 'bg-[#F9F8F8]'

  return (
    <LazyMotion features={domAnimation}>
      <m.section
        viewport={{ once: true, amount: 0.3 }}
        className={cn('landing-pages-wr section-spacing', isBgDark ? 'bg-[#F9F8F8]' : '', className)}
      >
        <SectionWrapper>
          <SectionHead title={title} description={description} className={sectionTitleClassName} />

          <m.div
            className='grid md:grid-cols-2 grid-cols-1 lg-mn:grid-cols-4 xl-min:!gap-5 lg-mn:gap-4 gap-6'
            variants={containerVariants}
          >
            {cards.map(card => {
              return (
                <m.article
                  key={card.id}
                  variants={cardVariants}
                  className={cn(
                    `flex flex-col py-5 px-2 [&>h3]:text-paragraph
                  [&>h3]:font-semibold xl-min:[&>h3]:text-xl [&>h3]:text-lg [&>h3]:mb-3 [&>h3]:mt-5 border-2 border-[#E5E5E5] 
                  transition-all rounded-md hover:border-[#0A9CFF] md-mn:text-left text-center`,
                    cardBackground
                  )}
                >
                  {card.image && (
                    <div className='md-mn:self-start'>
                      <ResponsiveImage
                        src={card.image.src}
                        alt={card.image.alt}
                        className={cn('h-[160px] w-[160px]', imgClassName)}
                      />
                    </div>
                  )}
                  <h3>{card.title}</h3>

                  <p className='text-paragraph leading-6 font-normal text-base'>{card.description}</p>
                </m.article>
              )
            })}
          </m.div>

          {ctaButton && (
            <LinkWithTransition href={ctaButton.href} className='mt-12 flex items-center justify-center'>
              <Button variant='call_to_action' size='lg' className='transition-all animate-bounce self-center'>
                {ctaButton.text}
              </Button>
            </LinkWithTransition>
          )}
        </SectionWrapper>
      </m.section>
    </LazyMotion>
  )
}

export default WhyWeSection
